/**
 * Dependencies.
 */
import { BrowserMultiFormatReader } from '@zxing/library';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';

import { deliveryService } from '@/api/delivery/delivery.service';
import { TimelineStepType } from '@/ui/components/timeline/timeline.component';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: {
    Timeline
  },
  name: 'scan-code-scene'
})
export default class extends Vue {
  /**
   * Data `code`.
   */
  code: string = null;

  /**
   * Data `canShowScanCodeModal`.
   */
  canShowScanCodeModal = false;

  /**
   * Data `codeReader`.
   */
  codeReader = new BrowserMultiFormatReader();

  /**
   * Data `isCameraLoading`.
   */
  isCameraLoading = true;

  /**
   * Getter timeline.
   */
  get timeline() {
    return [TimelineStepType.Current, TimelineStepType.Todo];
  }

  /**
   * Handle input enter.
   */
  async handleInputEnter() {
    if (this.code) {
      const valid = await this.handleValidateCode(this.code);

      if (valid) {
        this.handleGoToSelectType(this.code);
      }
    }
  }

  /**
   * Handle show scan code modal.
   */
  handleShowScanCodeModal() {
    this.isCameraLoading = true;
    this.canShowScanCodeModal = true;

    const { canEnumerateDevices = false } = this.codeReader || {};

    if (canEnumerateDevices) {
      this.$nextTick().then(async () => {
        (this.$refs.codeScanner as HTMLVideoElement).oncanplay = () => {
          this.isCameraLoading = false;
        };

        this.handleStartVideoDecoding();
      });
    }
  }

  /**
   * Handle hide scan code modal.
   */
  handleHideScanCodeModal() {
    this.canShowScanCodeModal = false;
  }

  /**
   * Handle start video decoding.
   */
  async handleStartVideoDecoding() {
    try {
      if (this.codeReader) {
        const result = await this.codeReader.decodeOnceFromVideoDevice(
          undefined,
          this.$refs.codeScanner as HTMLVideoElement
        );

        if (result && result.getText()) {
          const code = result.getText();

          const valid = await this.handleValidateCode(code);

          if (valid) {
            this.codeReader.reset();

            this.handleGoToSelectType(code);
          } else {
            this.handleStartVideoDecoding();
          }
        }
      }
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleStartVideoDecoding' });
      }
    }
  }

  /**
   * Handle validate code.
   */
  async handleValidateCode(code: string) {
    let valid = false;

    try {
      this.isLoading = true;

      const { result = false } =
        (await deliveryService.validateCode(code)) || {};

      valid = result;

      if (!result) {
        this.code = null;

        this.$kit.notify.error(
          this.i18n('DOCUMENT.LABELS.ERROR'),
          this.i18n('DOCUMENT.ERRORS.INVALID_CODE')
        );
      }
    } catch (error) {
      this.$kit.notify.error(
        this.i18n('DOCUMENT.LABELS.ERROR'),
        this.i18n('DOCUMENT.ERRORS.VALIDATING_CODE')
      );

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleValidateCode' });
      }
    } finally {
      this.isLoading = false;
    }

    return valid;
  }

  /**
   * Handle go to select type.
   */
  handleGoToSelectType(code: string) {
    this.$router.push({ name: 'SelectTypeDocument', params: { code } });
  }

  /**
   * Handle change can show scan code modal.
   */
  @Watch('canShowScanCodeModal')
  handleChangeCanShowScanCodeModal() {
    if (this.canShowScanCodeModal === false && this.codeReader) {
      this.codeReader.reset();
    }
  }
}
